import { ImageIcon, MoneyIcon } from "assets/icons";
import cn from "classnames";
import { DirectUploadMedia } from "libs/directUpload";
import { useGetVariantsByIds } from "modules/products";
import { useGetResellerById } from "modules/resellers";
import { ElementType } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Avatar } from "ui";

import { humanizeEntityType, humanizeFieldName, shortId } from "../../helpers";

import styles from "./EntityPreview.module.scss";

interface EntityPreviewProps {
  entityType?: string | null;
  entityId?: string | null;
  // The audit row's value snapshot (newValue ?? previousValue). Lets us show
  // meaningful info for entity types we can't fetch (Earning, Admin).
  snapshot?: Record<string, unknown> | null;
  compact?: boolean;
  // When true, the entity card links to that entity's own page (used on the
  // detail screen; the list rows are already links to the audit record).
  linkToEntity?: boolean;
}

interface EntityCardProps {
  image?: DirectUploadMedia | null;
  title: string;
  subtitle: string;
  compact?: boolean;
  rounded?: boolean;
  placeholder?: ElementType;
  to?: string;
}

const EntityCard = ({
  image,
  title,
  subtitle,
  compact,
  rounded,
  placeholder,
  to
}: EntityCardProps) => {
  const size = compact ? 36 : 52;

  const content = (
    <>
      <Avatar
        image={image ?? undefined}
        Placeholder={placeholder}
        dimensions={{
          width: size,
          height: size,
          borderRadius: rounded ? "50%" : "10px"
        }}
      />
      <div className={styles.card__text}>
        <span className={styles.card__title}>{title}</span>
        <span className={styles.card__subtitle}>{subtitle}</span>
      </div>
    </>
  );

  const className = cn(styles.card, {
    [styles["card--compact"]]: compact,
    [styles["card--link"]]: !!to
  });

  return to ? (
    <Link to={to} className={className}>
      {content}
    </Link>
  ) : (
    <div className={className}>{content}</div>
  );
};

const ProductVariantPreview = ({
  id,
  compact,
  linkToEntity
}: {
  id: string;
  compact?: boolean;
  linkToEntity?: boolean;
}) => {
  const { t } = useTranslation();
  const { variants } = useGetVariantsByIds([id]);
  const variant = variants[0];

  const categoryId = variant?.product?.categoryId;
  const to =
    linkToEntity && categoryId && variant?.productId
      ? `/products/details/${categoryId}/${variant.productId}/${variant.id}/boxes`
      : undefined;

  return (
    <EntityCard
      image={variant?.preview ?? null}
      title={variant?.name || shortId(id)}
      subtitle={t("Product variant")}
      compact={compact}
      placeholder={ImageIcon}
      to={to}
    />
  );
};

const ResellerPreview = ({
  id,
  compact,
  linkToEntity
}: {
  id: string;
  compact?: boolean;
  linkToEntity?: boolean;
}) => {
  const { t } = useTranslation();
  const { reseller } = useGetResellerById(id);

  const name = reseller
    ? `${reseller.firstname} ${reseller.lastname}`.trim()
    : "";

  return (
    <EntityCard
      image={reseller?.image ?? null}
      title={name || shortId(id)}
      subtitle={t("Reseller")}
      compact={compact}
      rounded
      to={linkToEntity ? `/users/all/${id}/mainInfo` : undefined}
    />
  );
};

const formatAmount = (value: unknown) =>
  value == null || isNaN(Number(value))
    ? null
    : `${Number(value).toLocaleString("fr-FR")} FCFA`;

const SimpleEntityPreview = ({
  entityType,
  entityId,
  snapshot,
  compact
}: {
  entityType: string;
  entityId: string;
  snapshot?: Record<string, unknown> | null;
  compact?: boolean;
}) => {
  const { t } = useTranslation();

  // Earning — show the payout amount + status from the snapshot.
  if (entityType === "Earning" && snapshot) {
    const amount = formatAmount(snapshot.amount);
    const status = snapshot.status
      ? humanizeFieldName(String(snapshot.status))
      : null;

    return (
      <EntityCard
        title={amount ?? t("Earning")}
        subtitle={[t("Earning"), status].filter(Boolean).join(" · ")}
        compact={compact}
        placeholder={MoneyIcon}
      />
    );
  }

  // Admin — show the moderator's name (or email) from the snapshot.
  if (entityType === "Admin" && snapshot) {
    const name =
      `${snapshot.firstName ?? ""} ${snapshot.lastName ?? ""}`.trim() ||
      String(snapshot.email ?? "");

    return (
      <EntityCard
        title={name || shortId(entityId)}
        subtitle={t("Admin")}
        compact={compact}
        rounded
      />
    );
  }

  return (
    <EntityCard
      title={t(humanizeEntityType(entityType))}
      subtitle={shortId(entityId)}
      compact={compact}
    />
  );
};

const EntityPreview = ({
  entityType,
  entityId,
  snapshot,
  compact,
  linkToEntity
}: EntityPreviewProps) => {
  if (!entityType || !entityId) {
    return <span className={styles.dash}>—</span>;
  }

  if (entityType === "ProductVariant") {
    return (
      <ProductVariantPreview
        id={entityId}
        compact={compact}
        linkToEntity={linkToEntity}
      />
    );
  }

  if (entityType === "Reseller") {
    return (
      <ResellerPreview
        id={entityId}
        compact={compact}
        linkToEntity={linkToEntity}
      />
    );
  }

  return (
    <SimpleEntityPreview
      entityType={entityType}
      entityId={entityId}
      snapshot={snapshot}
      compact={compact}
    />
  );
};

export default EntityPreview;
